import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Ghost, Clock, Send, X, MoreHorizontal, Check, RefreshCw } from 'lucide-react';
import { endpoints } from '../../api/api';

const GhostHunterView = ({ darkMode }) => {
    const [ghosts, setGhosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(null);

    const fetchGhosts = async () => {
        setLoading(true);
        try {
            const res = await endpoints.ghosts.getAll();
            setGhosts(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGhosts();
    }, []);

    const handleSend = async (trackerId, suggestionId) => {
        setProcessing(trackerId);
        try {
            await endpoints.ghosts.sendFollowUp(trackerId, suggestionId);
            // Remove from list
            setGhosts(prev => prev.filter(g => g.tracker.id !== trackerId));
        } catch (err) {
            console.error(err);
            alert("Failed to send follow-up");
        } finally {
            setProcessing(null);
        }
    };

    const handleSkip = async (trackerId) => {
        setProcessing(trackerId);
        try {
            await endpoints.ghosts.skip(trackerId);
            setGhosts(prev => prev.filter(g => g.tracker.id !== trackerId));
        } catch (err) {
            console.error(err);
        } finally {
            setProcessing(null);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-8 text-center opacity-50 space-y-3">
                <div className="animate-spin text-blue-500"><RefreshCw size={24} /></div>
                <p className="text-xs">Hunting for ghosts...</p>
            </div>
        );
    }

    if (ghosts.length === 0) {
        return (
            <div className={`flex flex-col items-center justify-center h-full p-8 text-center space-y-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <div className="p-4 rounded-full bg-slate-100 dark:bg-slate-800">
                    <Ghost size={32} className="opacity-50" />
                </div>
                <div>
                    <h3 className="text-sm font-medium mb-1">No Ghosts Found</h3>
                    <p className="text-xs opacity-70">Everyone has replied! Great job keeping up.</p>
                </div>
                <button
                    onClick={fetchGhosts}
                    className="flex items-center gap-2 text-xs text-blue-500 hover:text-blue-600"
                >
                    <RefreshCw size={12} /> Check Again
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-4 p-1">
            <div className="flex items-center justify-between px-2 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider opacity-50">
                    {ghosts.length} Ghosts Detected
                </span>
                <button onClick={fetchGhosts}><RefreshCw size={14} className="opacity-50 hover:opacity-100" /></button>
            </div>

            {ghosts.map((item) => {
                const { tracker, suggestions } = item;
                const daysAgo = Math.floor((new Date() - new Date(tracker.sent_at)) / (1000 * 60 * 60 * 24));

                return (
                    <motion.div
                        key={tracker.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, height: 0 }}
                        className={`rounded-xl border overflow-hidden ${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'}`}
                    >
                        {/* Header */}
                        <div className={`p-4 border-b ${darkMode ? 'border-white/5' : 'border-slate-100'}`}>
                            <div className="flex items-start justify-between mb-2">
                                <div>
                                    <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                        {tracker.recipient_name || tracker.recipient_email}
                                    </h3>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 font-medium">
                                            No Reply
                                        </span>
                                        <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                            {daysAgo}d ago
                                        </span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleSkip(tracker.id)}
                                    className="p-1 hover:bg-slate-100 dark:hover:bg-white/10 rounded"
                                >
                                    <X size={16} className="opacity-50" />
                                </button>
                            </div>
                            <div className={`text-xs italic p-2 rounded ${darkMode ? 'bg-black/20 text-slate-400' : 'bg-slate-50 text-slate-500'}`}>
                                "{tracker.subject}"
                            </div>
                        </div>

                        {/* Suggestions */}
                        <div className="p-3 space-y-3 bg-slate-50/50 dark:bg-black/20">
                            {suggestions.map((option) => (
                                <div
                                    key={option.id}
                                    className={`p-3 rounded-lg border transition-all hover:shadow-sm ${darkMode ? 'bg-slate-800 border-white/5 hover:bg-slate-700' : 'bg-white border-slate-200 hover:border-blue-200'
                                        }`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${option.option_type === 'gentle' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                                                option.option_type === 'value' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                                                    'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                                            }`}>
                                            {option.option_type}
                                        </span>
                                    </div>
                                    <p className={`text-xs mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                        {option.body}
                                    </p>
                                    <button
                                        onClick={() => handleSend(tracker.id, option.id)}
                                        disabled={processing === tracker.id}
                                        className={`w-full py-1.5 flex items-center justify-center gap-2 rounded text-xs font-medium transition-colors ${processing === tracker.id ? 'opacity-50 cursor-wait' : ''
                                            } ${darkMode ? 'bg-white text-slate-900 hover:bg-slate-200' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
                                    >
                                        {processing === tracker.id ? (
                                            <RefreshCw size={12} className="animate-spin" />
                                        ) : (
                                            <>
                                                <Send size={12} /> Send Follow-up
                                            </>
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
};

export default GhostHunterView;
